import { useState, useCallback, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import { listFeatured } from "@/lib/catalog.functions";
import { ProductCard } from "@/components/ProductCard";

const SITE = "https://kupish.si";

export const Route = createFileRoute("/izbrano")({
  head: () => ({
    meta: [
      { title: "Izbrani izdelki ponudnikov | kupish.si" },
      {
        name: "description",
        content:
          "Od vsakega ponudnika en naključno izbran izdelek. Odkrijte ponudbo vseh trgovin na kupish.si na enem mestu.",
      },
      { property: "og:title", content: "Izbrani izdelki ponudnikov" },
      {
        property: "og:description",
        content: "Od vsakega ponudnika en naključno izbran izdelek na kupish.si.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/izbrano` }],
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Vsebine ni bilo mogoče naložiti</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        Poskusite osvežiti stran ali si oglejte{" "}
        <Link to="/izdelki" className="text-accent underline">vse izdelke</Link>.
      </p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Strani ni mogoče najti</h1>
    </div>
  ),
  component: IzbraniIzdelki,
});

function IzbraniIzdelki() {
  const fetchFeatured = useServerFn(listFeatured);
  const [seed] = useState(() => Math.random().toString(36).slice(2));
  const { data, isLoading } = useQuery({
    queryKey: ["izbrano-one-per-vendor", seed],
    queryFn: () => fetchFeatured({ data: { onePerVendor: true, limit: 200 } }),
    staleTime: 0,
    gcTime: 0,
  });

  const [failed, setFailed] = useState<Set<string>>(new Set());
  const onUnavailable = useCallback((id: string) => {
    setFailed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const products = useMemo(
    () => (data?.products ?? []).filter((p) => !failed.has((p as { id: string }).id)),
    [data?.products, failed],
  );

  return (
    <div className="mx-auto max-w-7xl px-6 py-14 md:py-20">
      <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
        <div className="eyebrow">Izbrano za vas</div>
        <h1 className="font-serif text-3xl md:text-5xl mt-3">
          Vsi izbrani <span>izdelki</span>
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Od vsakega ponudnika z aktivno trgovino prikažemo en naključno izbran izdelek. Ob vsakem obisku
          vas čaka nov izbor.
        </p>
      </div>

      <div className="flex items-center justify-center pb-10">
        <div className="flex-1 h-px bg-border" />
        <Heart className="mx-4 h-5 w-5 text-accent shrink-0" strokeWidth={1.5} />
        <div className="flex-1 h-px bg-border" />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-square bg-muted" />
              <div className="mt-4 h-4 bg-muted rounded w-3/4 mx-auto" />
              <div className="mt-2 h-3 bg-muted rounded w-1/2 mx-auto" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">Trenutno ni nobenih izdelkov.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {products.map((product) => (
            <ProductCard
              key={(product as { id: string }).id}
              product={product as unknown as React.ComponentProps<typeof ProductCard>["product"]}
              onUnavailable={onUnavailable}
            />
          ))}
        </div>
      )}

      <div className="text-center mt-14">
        <Link
          to="/izdelki"
          className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-accent hover:text-accent/80 transition"
        >
          Poglej vse izdelke <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  );
}
